import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useMemo } from 'react';
import type { WebAuthnCredentialListItem } from '#api/webauthn-authenticators.ts';
import { Timestamp } from '#components/atoms/timestamp/Timestamp.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import { LastSeenAt } from '../../last-seen-at/LastSeenAt.tsx';
import { WebAuthnActionMenu } from '../action-menu/WebAuthnActionMenu.tsx';

type Props = {
  data: WebAuthnCredentialListItem[];
  isFetching: boolean;
  canDelete: boolean;
};

const columnHelper = createColumnHelper<WebAuthnCredentialListItem>();

export function WebAuthnList({ data, isFetching, canDelete }: Props) {
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        size: 160,
        cell(info) {
          const name = info.getValue();
          return name || <span className="i">unnamed</span>;
        },
        meta: {
          truncate: true,
        },
      }),
      columnHelper.accessor('authenticatorName', {
        header: 'Type',
        size: 200,
        cell(info) {
          return info.getValue() ?? 'Unknown';
        },
        meta: {
          truncate: true,
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created',
        size: 80,
        cell(info) {
          return <Timestamp datetime={info.getValue()} format="M/d/yyyy" />;
        },
      }),
      columnHelper.accessor('lastSeenAt', {
        header: 'Last used',
        size: 80,
        cell(info) {
          return <LastSeenAt lastSeenAt={info.getValue()} />;
        },
      }),
      columnHelper.display({
        id: 'action-menu',
        header: '',
        size: 34,
        cell(info) {
          if (!canDelete) return null;
          const { id } = info.row.original;
          return (
            <div className="flex justify-end">
              <WebAuthnActionMenu value={id} />
            </div>
          );
        },
      }),
    ],
    [canDelete]
  );

  return (
    <div className={cn({ 'opacity-dim': isFetching })}>
      <Table data={data} columns={columns} caption="Passkeys" className="mb-24 border-b" />
    </div>
  );
}
